/**
 * @fileoverview Aba "Cadastro do contato" do editor de triagem: o nome do
 * contato montado com os dados da triagem ("BR827372 Maria Souza", "Maria
 * Souza (SP)"), para a equipe achar a conversa pela busca. Quem configura
 * monta o formato com os botões dos dados (o número e os dados da OS, o nome do
 * cliente, o que a IA confirma nos itens) e vê como fica e quando muda. O
 * cliente continua sendo chamado só pelo nome. As regras ficam em
 * contactNameFormat.js (as mesmas do servidor).
 */

import React, { useMemo, useRef } from "react";
import TextField from "@material-ui/core/TextField";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";

import { i18n } from "../../../../translate/i18n";
import { translateWithLiterals } from "../../../../utils/translateWithLiterals";
import useStyles from "./styles";
import { LEGACY_CONTACT_NAME_FORMAT } from "./formState";
import {
  CONTACT_NAME_FORMAT_MAX,
  CONTACT_NAME_MAX_VARIABLES,
  contactNameProblem,
  insertToken,
  previewParts,
  tokenGroups,
  whenItChanges,
} from "./contactNameFormat";

const c = (key, params) => i18n.t(`triage.manager.editor.contact.${key}`, params);

// Código do problema → chave do aviso (a mesma do "Salvar").
const PROBLEM_KEYS = {
  empty: "contactNameEmpty",
  tooLong: "contactNameTooLong",
  lines: "contactNameLines",
  braces: "contactNameBraces",
  token: "contactNameToken",
  workOrder: "contactNameWorkOrder",
  noData: "contactNameNoData",
  tooMany: "contactNameTooMany",
};

/** O aviso de um problema do formato (aba e "Salvar"); o dado vai como foi digitado. */
export const contactNameProblemText = (problem) =>
  translateWithLiterals(`triage.manager.editor.errors.${PROBLEM_KEYS[problem.code]}`, {
    token: problem.token || "",
    max: problem.code === "tooMany" ? CONTACT_NAME_MAX_VARIABLES : CONTACT_NAME_FORMAT_MAX,
  });

/** Os exemplos da prévia: o número e o nome inventados, os dados da OS como na aba Conclusão. */
const examples = () => {
  const r = (key) => i18n.t(`triage.manager.editor.routes.placeholders.${key}`);
  return {
    os: "BR827372",
    nome: "Maria Souza",
    primeiro_nome: "Maria",
    "os.modelo": r("model").split("\n")[0],
    "os.marca": r("brand"),
    "os.categoria": r("category"),
    "os.tipo": r("type"),
    "os.garantia": r("warranty"),
    "os.cidade": r("city"),
    "os.uf": r("state"),
  };
};

/** "“A”", "“A” e “B”", "“A”, “B” e “C”". */
const itemList = (names) => {
  const quoted = names.map((n) => `“${n}”`);
  return quoted.length <= 1 ? quoted.join("") : `${quoted.slice(0, -1).join(", ")}${c("and")}${quoted[quoted.length - 1]}`;
};

const ContactEditor = ({ value, onChange, items, workOrdersEnabled, onGoToWorkOrders, onGoToItems }) => {
  const classes = useStyles();
  const inputRef = useRef(null);
  // A última seleção do campo: o botão põe o dado ali (sem ela, no fim).
  const selection = useRef(null);
  const set = (patch) => onChange({ ...value, ...patch });

  const ctx = useMemo(() => ({ items, workOrdersEnabled }), [items, workOrdersEnabled]);
  const groups = useMemo(() => tokenGroups(ctx), [ctx]);
  const problem = value.enabled ? contactNameProblem(value.format, ctx) : null;
  const preview = useMemo(() => previewParts(value.format, ctx, examples()), [value.format, ctx]);
  const when = useMemo(() => whenItChanges(value.format, ctx), [value.format, ctx]);

  // Ligar com o campo vazio: o formato que a opção antiga fazia, se há OS.
  const toggle = (enabled) => set({ enabled, format: enabled && !value.format.trim() && workOrdersEnabled ? LEGACY_CONTACT_NAME_FORMAT : value.format });

  const remember = (ev) => {
    selection.current = { start: ev.target.selectionStart, end: ev.target.selectionEnd };
  };

  const insert = (token) => {
    const el = inputRef.current;
    // Com o foco no campo (o botão não o tira), a seleção dele é a de agora.
    const focused = !!el && document.activeElement === el;
    const current = focused ? { start: el.selectionStart, end: el.selectionEnd } : selection.current;
    const { value: format, caret } = insertToken(value.format, current, token);
    set({ format });
    selection.current = { start: caret, end: caret };
    // Só volta ao campo se ele estava com o foco (no celular, não abre o teclado sozinho).
    if (focused) {
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(caret, caret);
      }, 0);
    }
  };

  const tokenLabel = (t) => {
    if (t.itemName !== undefined) return t.fieldLabel ? `${t.itemName} › ${t.fieldLabel}` : t.itemName;
    if (t.field) return i18n.t(`triage.manager.editor.routes.fields.${t.field}`);
    return c(`tokens.${t.token}`);
  };

  const tokenButton = (t) => {
    const label = tokenLabel(t);
    return (
      <button
        key={t.token}
        type="button"
        className={`${classes.dayBtn} ${classes.tokenBtn}`}
        // Sem tirar o foco do campo: o cursor fica onde estava.
        onMouseDown={(ev) => ev.preventDefault()}
        onClick={() => insert(t.token)}
        title={`{${t.token}}`}
        aria-label={translateWithLiterals("triage.manager.editor.contact.add", { label })}
      >
        <span>+ {label}</span>
      </button>
    );
  };

  return (
    <div className={classes.form}>
      <div className={classes.hint}>{c("intro")}</div>

      <FormControlLabel
        className={classes.check}
        control={<Switch color="primary" checked={value.enabled} onChange={(ev) => toggle(ev.target.checked)} />}
        label={c("enabled")}
      />

      {value.enabled && (
        <>
          <div>
            <TextField
              className={classes.field}
              variant="outlined"
              size="small"
              fullWidth
              multiline
              maxRows={3}
              label={c("format")}
              placeholder={c("formatPlaceholder")}
              value={value.format}
              inputRef={inputRef}
              inputProps={{ maxLength: CONTACT_NAME_FORMAT_MAX, spellCheck: false }}
              // Uma linha só: Enter não quebra, e o que vier colado com quebra vira espaço.
              onKeyDown={(ev) => {
                if (ev.key === "Enter") ev.preventDefault();
              }}
              onChange={(ev) => {
                remember(ev);
                set({ format: ev.target.value.replace(/[\r\n\t]+/g, " ") });
              }}
              onSelect={remember}
              error={!!problem}
            />
            <div className={classes.hint} style={{ marginTop: 6 }}>
              {c("formatHint")}
            </div>
            {problem && (
              <div className={classes.warnText} role="alert" style={{ marginTop: 4 }}>
                {contactNameProblemText(problem)}
              </div>
            )}
          </div>

          <div className={classes.tokenGroups}>
            <div>
              <div className={classes.tokenGroupTitle}>{c("groups.contact")}</div>
              <div className={classes.dayRow}>{groups.contact.map(tokenButton)}</div>
            </div>
            <div>
              <div className={classes.tokenGroupTitle}>{c("groups.workOrder")}</div>
              {groups.workOrder.length ? (
                <div className={classes.dayRow}>{groups.workOrder.map(tokenButton)}</div>
              ) : (
                <div className={classes.hint}>
                  {c("workOrderOff")}{" "}
                  {onGoToWorkOrders && (
                    <button type="button" className={classes.link} onClick={onGoToWorkOrders}>
                      {c("workOrderOpen")}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div>
              <div className={classes.tokenGroupTitle}>{c("groups.items")}</div>
              {groups.items.length ? (
                <div className={classes.dayRow}>{groups.items.map(tokenButton)}</div>
              ) : (
                <div className={classes.hint}>{c("itemsNone")}</div>
              )}
              <div className={classes.hint} style={{ marginTop: 6 }}>
                {c("itemsExcluded")}
              </div>
            </div>
          </div>

          <div>
            <div className={classes.label}>{c("preview")}</div>
            <div className={classes.tplPreview} data-contact-name-preview>
              {preview.map((p, i) => (
                <span
                  key={i}
                  className={p.type === "placeholder" ? classes.previewPlaceholder : p.type === "invalid" ? classes.previewInvalid : undefined}
                >
                  {p.text}
                </span>
              ))}
            </div>
            {when && (
              <div className={classes.hint} style={{ marginTop: 6 }} data-contact-name-when>
                <strong>{c("when.title")}</strong>{" "}
                {when.moment === "start"
                  ? c("when.start")
                  : when.moment === "items"
                    ? translateWithLiterals("triage.manager.editor.contact.when.items", { items: itemList(when.items) })
                    : c("when.end")}
                {when.moment === "end" && (
                  <>
                    {" "}
                    {c("when.linkHint")}{" "}
                    {onGoToItems && (
                      <button type="button" className={classes.link} onClick={onGoToItems}>
                        {c("when.openItems")}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <div className={classes.noteBox}>
            <div>{c("notes.customer")}</div>
            <div>{c("notes.once")}</div>
            <div>{c("notes.missing")}</div>
          </div>
        </>
      )}
    </div>
  );
};

export default ContactEditor;
